import React, { useState } from 'react';
import { Modal, Button, Typography, Select, Checkbox, useToast, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { SearchableSelect } from '../../common/SearchableSelect';
import { UserPlus } from 'lucide-react';
import { ConfirmationDialog } from '../../common/dialogs/ConfirmationDialog';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { useBulkAssignContacts } from '../../../hooks/contacts/useBulkAssignContacts';
import { buildUserOptionsByPhone } from '../../../utils/userOptions';

interface BulkAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BulkAssignModal: React.FC<BulkAssignModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { data: tags = [], isLoading: isLoadingTags, hasMore: tagsHasMore, loadMore: tagsLoadMore, setSearch: tagsSetSearch } = useUniqueContactTags(isOpen);
  const { users, loading: isLoadingUsers } = useOrganizationUsers();
  const { assignContacts, isLoading: isAssigning } = useBulkAssignContacts();
  const { showToast } = useToast();

  const [selectedTag, setSelectedTag] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [reAssign, setReAssign] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const tagOptions = (tags || []).map((tag) => ({ value: tag, label: tag }));

  // Find the selected user's details
  const selectedUserDetails = users.find((user) => user.phone_number === selectedUser);

  const handleConfirmAction = async () => {
    if (!selectedUserDetails) {
      showToast({
        message: 'Please select a team member to assign contacts to',
        variant: 'error',
      });
      return;
    }

    if (!selectedTag) {
      showToast({
        message: 'Please select a tag to filter contacts',
        variant: 'error',
      });
      return;
    }

    try {
      // Call the hook to assign contacts
      const data = await assignContacts({
        name: `${selectedUserDetails.firstName} ${selectedUserDetails.lastName}`.trim(),
        phoneNumber: selectedUserDetails.phone_number,
        email: selectedUserDetails.email,
        tag: selectedTag || undefined,
        reAssign: reAssign,
      });

      showToast({
        message: `Successfully assigned ${data.updated} contacts to ${selectedUserDetails.firstName} ${selectedUserDetails.lastName}. ${data.skipped} contacts were skipped.`,
        variant: 'success',
      });

      // Refresh the contacts list
      onSuccess?.();

      // Reset form and close modals
      setSelectedTag('');
      setSelectedUser('');
      setReAssign(false);
      setShowConfirmation(false);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showToast({
        message: `Error assigning contacts: ${errorMessage}`,
        variant: 'error',
      });
      setShowConfirmation(false);
    }
  };

  const handleCancel = () => {
    setSelectedTag('');
    setSelectedUser('');
    setReAssign(false);
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="sm">
        <Modal.Header>
          <div className="flex items-center gap-3">
            <Typography variant="h4" className="font-bold">
              Bulk Claim Contacts
            </Typography>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <Alert variant="info">
              <AlertDescription>
                Claim contacts for a team member. You must select a tag to filter which contacts will
                be claimed.
              </AlertDescription>
            </Alert>

            <div className="pt-2">
              <Typography variant="small" className="font-medium mb-1 block">
                Claim For:
              </Typography>
              <Select
                searchable
                options={buildUserOptionsByPhone(users)}
                value={selectedUser}
                onChange={(value) => setSelectedUser(value as string)}
                placeholder={isLoadingUsers ? 'Loading team members...' : 'Select a team member'}
                className="w-full"
                isLoading={isLoadingUsers}
              />
            </div>

            <div className="pt-2">
              <Typography variant="small" className="font-medium mb-1 block">
                Filter by Tag:
              </Typography>
              <SearchableSelect
                options={tagOptions}
                value={selectedTag}
                onChange={(value) => setSelectedTag(value)}
                placeholder={isLoadingTags ? 'Loading tags...' : 'Select a tag'}
                className="w-full"
                isLoading={isLoadingTags}
                hasMore={tagsHasMore}
                onLoadMore={tagsLoadMore}
                onSearch={tagsSetSearch}
                searchPlaceholder="Search tags..."
              />
            </div>

            <div className="pt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reAssign"
                  checked={reAssign}
                  onChange={(e) => setReAssign(e.target.checked)}
                />
                <label htmlFor="reAssign" className="text-sm cursor-pointer">
                  Re-claim contacts that are already claimed by someone else
                </label>
              </div>
              <Typography variant="small" className="text-gray-500 dark:text-gray-400 mt-1 block">
                If unchecked, only unclaimed contacts will be claimed.
              </Typography>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel} size="sm" disabled={isAssigning}>
              Cancel
            </Button>
            <Button
              onClick={() => setShowConfirmation(true)}
              size="sm"
              className="bg-primary hover:bg-primary/80 text-white flex items-center gap-2"
              disabled={!selectedUser || !selectedTag || isAssigning}
              isLoading={isAssigning}
            >
              <UserPlus className="w-4 h-4" />
              Claim Contacts
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => !isAssigning && setShowConfirmation(false)}
        onConfirm={handleConfirmAction}
        title="Confirm Bulk Claim"
        message={`Are you sure you want to claim all contacts with the tag "${selectedTag}" for ${selectedUserDetails?.firstName} ${selectedUserDetails?.lastName}?${
          reAssign
            ? ' This will re-claim contacts that are already claimed by someone else.'
            : ' Only unclaimed contacts will be affected.'
        }`}
        confirmLabel="Assign Contacts"
        variant="warning"
        isLoading={isAssigning}
      />
    </>
  );
};

export default BulkAssignModal;
